import type {
  TableFeatures,
  RowData,
  Column_Internal,
} from '@tanstack/react-table';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import type { JSX } from 'react';

import * as styles from './DataTablePinningShadowCells.sty.js';
import { DATA_TABLE_PINNING_SHADOW_CELL_ROW_ID_SUFFIX } from '../../constants.js';
import { useDataTableContent } from '../../contexts/DataTableContext.js';

interface DataTablePinningShadowCellsProps<
  TFeatures extends TableFeatures,
  TData extends RowData,
> {
  lastLeftPinnedColumn: {
    column?: Column_Internal<TFeatures, TData>;
    index?: number;
  };
  firstRightPinnedColumn: {
    column?: Column_Internal<TFeatures, TData>;
    index?: number;
  };
  gridRowPosition: {
    gridColumnStart: number;
    gridColumnEnd: number | `span ${number}` | 'row-end';
    gridRowStart: number;
    gridRowEnd: number | `span ${number}`;
  };
  rowId: string;
}

/**
 * Renders a shadow to visually emphasize pinned columns in the body.
 * To place the shadow correctly, we render a cell on top of the last left pinned/first right pinned cell
 * and add the shadow inside an `::after` element to the right/left edge of this cell. The shadow is only
 * rendered when the table is actually overflowing behind the pinned columns on the respective side.
 */
export function DataTablePinningShadowCells<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  props: Readonly<DataTablePinningShadowCellsProps<TFeatures, TData>>,
): JSX.Element | null {
  const { gridRowPosition, rowId } = props;

  const tableContent = useDataTableContent();

  const leftShadowCellShouldRender =
    props.lastLeftPinnedColumn.column !== undefined &&
    tableContent.hasOverflowLeft;
  const rightShadowCellShouldRender =
    props.firstRightPinnedColumn.column !== undefined &&
    tableContent.hasOverflowRight;

  // Determine positions for left shadow
  const leftPosition =
    leftShadowCellShouldRender &&
    props.lastLeftPinnedColumn.column?.getLeftPinnedPosition();

  // Determine grid positions for the shadow cells
  const lastLeftPinnedColumnGridPosition =
    leftShadowCellShouldRender &&
    props.lastLeftPinnedColumn.column?.getGridColumnPosition(
      props.lastLeftPinnedColumn.index ?? 0,
    );

  // Determine grid positions for the shadow cells
  const firstRightPinnedColumnGridPosition =
    rightShadowCellShouldRender &&
    props.firstRightPinnedColumn.column?.getGridColumnPosition(
      props.firstRightPinnedColumn.index ?? 0,
    );

  // Determine positions for right shadow
  const rightPosition =
    rightShadowCellShouldRender &&
    props.firstRightPinnedColumn.column?.getRightPinnedPosition();

  // Create left shadow cell
  const leftShadowCell = leftShadowCellShouldRender ? (
    <div
      key="left"
      role="presentation"
      className={styles.pinningShadowCell.left}
      style={{
        ...assignInlineVars({
          [styles.leftPositionVar]: `${leftPosition}px`,
        }),
        ...gridRowPosition,
        ...lastLeftPinnedColumnGridPosition,
      }}
      data-rowid={`${rowId}${DATA_TABLE_PINNING_SHADOW_CELL_ROW_ID_SUFFIX}`}
      data-pinned-position="left"
    />
  ) : null;

  // Create right shadow cell
  const rightShadowCell = rightShadowCellShouldRender ? (
    <div
      key="right"
      role="presentation"
      className={styles.pinningShadowCell.right}
      style={{
        ...assignInlineVars({
          [styles.rightPositionVar]: `${rightPosition}px`,
        }),
        ...gridRowPosition,
        ...firstRightPinnedColumnGridPosition,
      }}
      data-rowid={`${rowId}${DATA_TABLE_PINNING_SHADOW_CELL_ROW_ID_SUFFIX}`}
      data-pinned-position="right"
    />
  ) : null;

  return (
    <>
      {leftShadowCell}
      {rightShadowCell}
    </>
  );
}
DataTablePinningShadowCells.displayName = 'DataTablePinningShadowCells';
